import { toast } from '@hakimo-ui/hakimo/util';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { MicSession, startMicSession } from './mic-session';

export type Stopped = { kind: 'stopped'; file: File };
export type RecorderState =
  | { kind: 'acquiring' }
  | { kind: 'recording' }
  | Stopped;

export function useMicRecorder(onAcquireFail: () => void) {
  const [state, setState] = useState<RecorderState>({ kind: 'acquiring' });
  const [attempt, setAttempt] = useState(0);
  const sessionRef = useRef<MicSession | null>(null);

  useEffect(() => {
    let cancelled = false;
    const isRestart = attempt > 0;

    startMicSession()
      .then((session) => {
        if (cancelled) {
          void session.stop();
          return;
        }
        sessionRef.current = session;
        setState({ kind: 'recording' });
      })
      .catch((error) => {
        if (cancelled) return;
        toast('Unable to access microphone', { type: 'error' });
        if (isRestart) return;
        Sentry.captureMessage('Unable to access microphone', {
          extra: { error },
        });
        onAcquireFail();
      });

    return () => {
      cancelled = true;
      void sessionRef.current?.stop();
      sessionRef.current = null;
    };
  }, [attempt, onAcquireFail]);

  const stop = async () => {
    const session = sessionRef.current;
    if (!session) return;
    const blob = await session.stop();
    const file = new File([blob], `${Date.now()}.webm`, { type: 'audio/webm' });
    setState({ kind: 'stopped', file });
  };

  const restart = () => {
    setState({ kind: 'acquiring' });
    setAttempt((a) => a + 1);
  };

  return { state, stop, restart };
}
